import { PersonAdd } from '@fs/zion-icon'
import { LayoutBand, Button, Cell, colors, FlowGrid, H5, List, Row, Separator, Subheading, Divider } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import PersonDisplay from './PersonDisplay'
import NamesOnOtherImages from './NamesOnOtherImages'
import { formatRecordDisplayData, getHeadOfHouseholdName, getPrimaryPerson } from '../utils/record.utils'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { sortPersonsByPrimary } from '../utils/display.utils'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import NoPrimaryAlert, { getNoPrimaryVariant } from '../../shared/components/NoPrimaryAlert'
import useGroupSectionsRework from '../hooks/useGroupSectionsRework'
import useAddNamesEntryPoint, { ADD_NAMES_ENTRY_POINTS } from '../../../../hooks/useAddNamesEntryPoint'

/**
 * HouseholdDisplay
 *
 * Displays a household/record with its associated persons.
 *
 * Before the group sections rework: a grey card header with the record details, plus "Add Person"
 * buttons.
 *
 * After it: groups are told apart by a divider and the Relationships card is the way into an
 * existing group, so the header's job of naming the group passes to the region's aria-label.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.household - The household object to display
 * @param {Array} props.persons - Array of person objects belonging to this household from the useHouseholdList hook
 * @param {Function} props.onPersonSelect - Callback when a person in the list is selected
 * @returns {JSX.Element}
 */
const HouseholdDisplay = ({ household, persons, onPersonSelect }) => {
  const [t] = useTranslation()
  const groupSectionsRework = useGroupSectionsRework()
  const { highlightedPersonId } = useR2StateContext()
  const { isReadOnlyCollection } = useCollectionContextProvider()
  const { open: openAddNames } = useAddNamesEntryPoint(ADD_NAMES_ENTRY_POINTS.BLANK_STATE)

  const primaryPersonName = getHeadOfHouseholdName(household)
  const { primaryEvent, primaryEventSummary } = formatRecordDisplayData(household)
  const hasPrimaryEvent = !!primaryEvent
  const hasPrimaryPerson = !!getPrimaryPerson(household)
  const sortedPersons = sortPersonsByPrimary(persons)

  const groupLabel = primaryPersonName
    ? t('household-display.group-region-with-name', '{primaryPersonName} group', { primaryPersonName })
    : t('household-display.group-region', 'Group')

  const members = (
    <>
      {groupSectionsRework && <Divider marginY="xxs" fullWidth />}
      {/* The group's own divider supplies the leading gap above the alert. */}
      <NoPrimaryAlert
        variant={getNoPrimaryVariant({ hasPrimaryPerson, hasPrimaryEvent })}
        gutters={groupSectionsRework ? 'below' : 'both'}
      />
      <List>
        {sortedPersons.map((person) => (
          <PersonDisplay
            key={person.id}
            person={person}
            record={household}
            onPersonSelect={onPersonSelect}
            isSelected={highlightedPersonId === person?.id}
          />
        ))}
        <NamesOnOtherImages household={household} />
      </List>
    </>
  )

  if (groupSectionsRework) {
    return (
      <section aria-label={groupLabel} data-testid="group-region">
        {members}
      </section>
    )
  }

  return (
    <>
      <LayoutBand color={colors.gray02} marginY="xs">
        <Row>
          <Cell growX alignY="middle">
            <H5>
              {primaryPersonName
                ? t('household-display.household-with-name', '{primaryPersonName} Household', { primaryPersonName })
                : t('household-display.household', 'Household')}
              {primaryEventSummary && <Subheading>{primaryEventSummary}</Subheading>}
            </H5>
          </Cell>
        </Row>
      </LayoutBand>
      {members}

      <Separator size="nano" />
      <FlowGrid columnCount={1} orientation="vertical" gutters="xs">
        <Row>
          <Button
            emphasis="medium"
            color="blue"
            Icon={PersonAdd}
            onClick={() => openAddNames({ recordId: household.id })}
            linkName="Add to Household"
            data-testid="add-person-footer-button"
            disabled={isReadOnlyCollection}
          >
            {t('household-display.add-person-footer-button', 'Add Person')}
          </Button>
        </Row>
      </FlowGrid>
    </>
  )
}

export default HouseholdDisplay
